import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { stockTransfersApi } from "@/modules/inventory/presentation/api/client";
import { useStockTransfersQuery } from "@/modules/inventory/presentation/api/queries";
import { downloadTransfersCsv } from "@/modules/inventory/presentation/ui/transfers/export-transfers-csv";
import { TransfersFilter } from "@/modules/inventory/presentation/ui/transfers/TransfersFilter";
import { TransfersSummaryCards } from "@/modules/inventory/presentation/ui/transfers/TransfersSummaryCards";
import { TransfersTable } from "@/modules/inventory/presentation/ui/transfers/TransfersTable";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Loader } from "@/shared/ui/Loader";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { localDayEndISO, localDayStartISO } from "@/shared/lib/date-time";
import { TRANSFER_MENU_TITLE } from "@/modules/inventory/presentation/ui/transfers/transferLaoLabels";
import {
  TRANSFER_LIST_SHELL,
  TRANSFER_SECTION_STACK,
} from "@/modules/inventory/presentation/ui/transfers/transferCardLayout";
import { DownloadIcon, PackageIcon } from "lucide-react";

type TransfersSearch = {
  status?: string;
  fromBranchId?: string;
  toBranchId?: string;
  submittedFrom?: string;
  submittedTo?: string;
  offset?: number;
  limit?: number;
};

export function TransfersPage() {
  const nav = useNavigate({ from: "/app/inventory/transfers" });
  const search = useSearch({ from: "/app/inventory/transfers" }) as TransfersSearch;

  const params = {
    status: search.status as Parameters<
      typeof stockTransfersApi.list
    >[0]["status"],
    fromBranchId: search.fromBranchId,
    toBranchId: search.toBranchId,
    submittedFrom: search.submittedFrom
      ? localDayStartISO(search.submittedFrom)
      : undefined,
    submittedTo: search.submittedTo
      ? localDayEndISO(search.submittedTo)
      : undefined,
    limit: search.limit ?? 20,
    offset: search.offset ?? 0,
  };

  const { data, isLoading } = useStockTransfersQuery(params);

  const agingSubmitted = useQuery({
    queryKey: ["inventory", "transfers", "aging", "submitted"],
    queryFn: () =>
      stockTransfersApi.list({ status: "SUBMITTED", limit: 100, offset: 0 }),
  });
  const agingShipped = useQuery({
    queryKey: ["inventory", "transfers", "aging", "shipped"],
    queryFn: () =>
      stockTransfersApi.list({ status: "SHIPPED", limit: 100, offset: 0 }),
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const submittedPending = agingSubmitted.data?.total ?? 0;
  const shippedUnreceived = agingShipped.data?.total ?? 0;

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: TRANSFER_MENU_TITLE,
              to: "/app/inventory/transfers",
            },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="flex items-center gap-2 font-bold text-2xl tracking-tight">
              <PackageIcon className="size-5" />
              {TRANSFER_MENU_TITLE} (Hub ໄປສາຂາ)
            </h2>
            <p className="text-muted-foreground">
              ຄິວອະນຸມັດ · ຈັດສົ່ງ · ຕິດຕາມຄຳຂໍຄ້າງ
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            disabled={items.length === 0}
            onClick={() => downloadTransfersCsv(items)}
          >
            <DownloadIcon className="mr-2 size-4" />
            ດາວໂຫຼດ CSV
          </Button>
        </div>

        <div className={TRANSFER_SECTION_STACK}>
          <TransfersSummaryCards
            submittedPending={submittedPending}
            shippedUnreceived={shippedUnreceived}
            filteredTotal={total}
            isLoading={
              agingSubmitted.isLoading ||
              agingShipped.isLoading ||
              (isLoading && items.length === 0)
            }
          />

          <div className={TRANSFER_LIST_SHELL}>
          <TransfersFilter />
          {isLoading && items.length === 0 ? (
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          ) : (
            <>
              <TransfersTable
                data={items}
                onOpen={(row) =>
                  nav({
                    to: "/app/inventory/transfers/$id",
                    params: { id: row.id },
                  })
                }
              />
              <div className="flex items-center justify-between border-t px-4 py-3 text-sm">
                <span className="text-muted-foreground">
                  {total} ລາຍການ · ເລີ່ມທີ {params.offset + 1}
                </span>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={params.offset <= 0}
                    onClick={() =>
                      nav({
                        search: {
                          ...search,
                          offset: Math.max(0, params.offset - params.limit),
                        },
                      })
                    }
                  >
                    ກ່ອນໜ້າ
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={params.offset + params.limit >= total}
                    onClick={() =>
                      nav({
                        search: {
                          ...search,
                          offset: params.offset + params.limit,
                        },
                      })
                    }
                  >
                    ຖັດໄປ
                  </Button>
                </div>
              </div>
            </>
          )}
          </div>
        </div>
      </Main>
    </>
  );
}
